import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { useHelpdeskLanguage } from '../../context/HelpdeskLanguageContext';
import './Helpdesk.css';

function HelpdeskDashboard() {
    const { t, language, toggleLanguage, isRTL } = useHelpdeskLanguage();
    const [searchParams, setSearchParams] = useSearchParams();
    
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            window.location.href = '/login';
        }
    }, []);
    
    const [stats, setStats] = useState(null);
    const [slaStats, setSlaStats] = useState({
        isAdminOrSupervisor: false,
        all: { level1: 0, level2: 0, level3: 0, total: 0 },
        my: { level1: 0, level2: 0, level3: 0, total: 0 }
    });
    
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [canViewAllDepartments, setCanViewAllDepartments] = useState(false);
    const [agents, setAgents] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 100, total: 0, pages: 0 });
    
    const [activeCardLabel, setActiveCardLabel] = useState(null);

const getEmptyFilters = () => ({
    search: '',
    status: ['open', 'assigned', 'in_progress', 'under_development', 'waiting_for_customer'],
    priority: '',
    category_id: '',
    department_id: '',
    assigned_to: '',
    date_from: '',
    date_to: '',
    hide_closed: true,
    sla_level: '',
    delayed: false
});

const savedDashFilters = JSON.parse(sessionStorage.getItem('helpdesk_dash_filters') || 'null');
const [filters, setFilters] = useState(savedDashFilters || getEmptyFilters());

const savedDashSort = JSON.parse(sessionStorage.getItem('helpdesk_dash_sort') || 'null');
const [sortConfig, setSortConfig] = useState(savedDashSort || {
    field: 'created_at',
    direction: 'desc'
});


    useEffect(() => {
        loadDashboardStats();
        loadSlaStats();
        loadCategories();
        loadMyDepartments();
        loadAgents();
    }, []);

    useEffect(() => {
        loadTickets();
    }, [filters, pagination.page, sortConfig]);

useEffect(() => {
    sessionStorage.setItem('helpdesk_dash_filters', JSON.stringify(filters));
}, [filters]);

useEffect(() => {
    sessionStorage.setItem('helpdesk_dash_sort', JSON.stringify(sortConfig));
}, [sortConfig]);


    const handleAuthError = (error) => {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            return true;
        }
        if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error') || !error.response) {
            return true;
        }
        return false;
    };

    const loadDashboardStats = async () => {
        try {
            const response = await api.get('/helpdesk-agent/dashboard');
            if (response.data.success) {
                const s = response.data.stats;
                // Compute active/completed on frontend as fallback if backend doesn't return them
                if (s.active_tickets === undefined) {
                    s.active_tickets = (s.open_tickets || 0) + (s.assigned_tickets || 0) + (s.in_progress_tickets || 0) + (s.under_development_tickets || 0) + (s.waiting_tickets || 0);
                }
                if (s.completed_tickets === undefined) {
                    s.completed_tickets = (s.solved_pending_tickets || 0) + (s.resolved_tickets || 0) + (s.closed_tickets || 0);
                }
                setStats(s);
            }
        } catch (error) {
            if (!handleAuthError(error)) {
                console.error('Failed to load dashboard stats:', error);
            }
        }
    };

    const loadSlaStats = async () => {
        try {
            const response = await api.get('/helpdesk-agent/sla-stats');
            if (response.data.success) {
                setSlaStats(response.data);
            }
        } catch (error) {
            if (!handleAuthError(error)) {
                console.error('Failed to load SLA stats:', error);
            }
        }
    };

    const loadCategories = async () => {
        try {
            const response = await api.get('/helpdesk-agent/categories');
            if (response.data.success) {
                setCategories(response.data.categories || []);
            }
        } catch (error) {
            if (!handleAuthError(error)) {
                console.error('Failed to load categories:', error);
            }
        }
    };

    const loadMyDepartments = async () => {
        try {
            const response = await api.get('/helpdesk-agent/my-departments');
            if (response.data.success) {
                const depts = response.data.departments || [];
                setDepartments(depts);
                setCanViewAllDepartments(response.data.canViewAll || false);
                
                const savedDeptId = localStorage.getItem('helpdesk_last_department');
                if (savedDeptId) {
                    const isValidDept = depts.some(d => d.id.toString() === savedDeptId);
                    if (isValidDept || response.data.canViewAll) {
                        setFilters(prev => ({ ...prev, department_id: savedDeptId }));
                    } else {
                        localStorage.removeItem('helpdesk_last_department');
                    }
                }
            }
        } catch (error) {
            if (!handleAuthError(error)) {
                console.error('Failed to load departments:', error);
            }
        }
    };

    const loadAgents = async () => {
        try {
            const response = await api.get('/helpdesk-agent/agents');
            if (response.data.success) {
                setAgents(response.data.agents || []);
            }
        } catch (error) {
            if (!handleAuthError(error)) {
                console.error('Failed to load agents:', error);
            }
        }
    };

    const loadTickets = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            
            if (filters.search) params.append('search', filters.search);
            if (filters.status.length > 0) params.append('status', filters.status.join(','));
            if (filters.priority) params.append('priority', filters.priority);
            if (filters.category_id) params.append('category_id', filters.category_id);
            if (filters.department_id) params.append('department_id', filters.department_id);
            if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
            if (filters.date_from) params.append('date_from', filters.date_from);
            if (filters.date_to) params.append('date_to', filters.date_to);
            if (filters.hide_closed) params.append('hide_closed', 'true');
            if (filters.sla_level) params.append('sla_level', filters.sla_level);
            if (filters.delayed) params.append('delayed', 'true');
            
            params.append('page', pagination.page);
            params.append('limit', pagination.limit);
            params.append('sort_field', sortConfig.field);
            params.append('sort_direction', sortConfig.direction);

            const response = await api.get(`/helpdesk-agent/tickets?${params.toString()}`);
            if (response.data.success) {
                setTickets(response.data.tickets);
                setPagination(prev => ({ ...prev, ...response.data.pagination }));
            }
        } catch (error) {
            if (!handleAuthError(error)) {
                console.error('Failed to load tickets:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    // ============================================
    // FILTER HANDLERS
    // ============================================

    const handleDropdownChange = (name, value) => {
        const newFilters = getEmptyFilters();
        newFilters.status = filters.status;
        if (name !== 'department_id') {
            newFilters.department_id = filters.department_id;
        }
        newFilters[name] = value;
        
        if (name === 'department_id') {
            if (value) {
                localStorage.setItem('helpdesk_last_department', value);
            } else {
                localStorage.removeItem('helpdesk_last_department');
            }
        }
        
        setFilters(newFilters);
        setActiveCardLabel(null);
        setPagination(prev => ({ ...prev, page: 1 }));
        setSortConfig({ field: 'created_at', direction: 'desc' });
    };

    const handleStatusToggle = (status) => {
        setFilters(prev => {
            const newStatuses = prev.status.includes(status)
                ? prev.status.filter(s => s !== status)
                : [...prev.status, status];
            
            return { 
                ...prev, 
                status: newStatuses,
                sla_level: '',
                delayed: false,
                assigned_to: '',
                date_from: '',
                date_to: ''
            };
        });
        setActiveCardLabel(null);
        setPagination(prev => ({ ...prev, page: 1 }));
        setSortConfig({ field: 'created_at', direction: 'desc' });
    };

    const handleHideClosedChange = (checked) => {
        setFilters(prev => ({ ...prev, hide_closed: checked }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleSearchChange = (value) => {
        setFilters(prev => ({ ...prev, search: value }));
    };

    const handleSort = (field) => {
        setSortConfig(prev => ({
            field,
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const getSortIcon = (field) => {
        if (sortConfig.field !== field) return '↕';
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPagination(prev => ({ ...prev, page: 1 }));
        loadTickets();
    };

    const clearFilters = () => {
    sessionStorage.removeItem('helpdesk_dash_filters');
    sessionStorage.removeItem('helpdesk_dash_sort');
    const emptyFilters = getEmptyFilters();
    
        const savedDeptId = localStorage.getItem('helpdesk_last_department');
        if (savedDeptId) {
            emptyFilters.department_id = savedDeptId;
        }
        setFilters(emptyFilters);
        setActiveCardLabel(null);
        setSortConfig({ field: 'created_at', direction: 'desc' });
        setPagination(prev => ({ ...prev, page: 1 }));
        setSearchParams({});
    };

    // ============================================
    // CARD CLICK HANDLERS
    // ============================================

    const handleActiveClick = () => {
        const newFilters = getEmptyFilters();
        newFilters.status = ['open', 'assigned', 'in_progress', 'under_development', 'waiting_for_customer'];
        newFilters.department_id = filters.department_id;
        setFilters(newFilters);
        setActiveCardLabel(language === 'ar' ? 'نشطة' : 'Active');
        setSortConfig({ field: 'created_at', direction: 'desc' });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleCompletedClick = () => {
        const newFilters = getEmptyFilters();
        newFilters.status = ['solved_pending', 'resolved', 'closed'];
        newFilters.hide_closed = false;
        newFilters.department_id = filters.department_id;
        setFilters(newFilters);
        setActiveCardLabel(language === 'ar' ? 'مكتملة' : 'Completed');
        setSortConfig({ field: 'created_at', direction: 'desc' });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleUnassignedClick = () => {
        const newFilters = getEmptyFilters();
        newFilters.assigned_to = 'unassigned';
        newFilters.department_id = filters.department_id;
        setFilters(newFilters);
        setActiveCardLabel(language === 'ar' ? 'غير مسندة' : 'Unassigned');
        setSortConfig({ field: 'created_at', direction: 'desc' });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleMyTicketsClick = () => {
        const newFilters = getEmptyFilters();
        newFilters.assigned_to = 'me';
        newFilters.department_id = filters.department_id;
        setFilters(newFilters);
        setActiveCardLabel(language === 'ar' ? 'تذاكري' : 'My Tickets');
        setSortConfig({ field: 'created_at', direction: 'desc' });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleTodayClick = () => {
        const today = new Date().toISOString().split('T')[0];
        const newFilters = getEmptyFilters();
        newFilters.date_from = today;
        newFilters.date_to = today;
        newFilters.department_id = filters.department_id;
        setFilters(newFilters);
        setActiveCardLabel(language === 'ar' ? 'اليوم' : 'Today');
        setSortConfig({ field: 'created_at', direction: 'desc' });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleSlaCardClick = (level) => {
        const newFilters = getEmptyFilters();
        newFilters.department_id = filters.department_id;
        
        if (level === 'all') {
            newFilters.delayed = true;
            setActiveCardLabel(language === 'ar' ? 'جميع المتأخرة' : 'All Delayed');
        } else {
            newFilters.sla_level = level.toString();
            const levelNames = {
                1: language === 'ar' ? 'متأخر' : 'Delayed',
                2: language === 'ar' ? 'متجاوز' : 'Overdue',
                3: language === 'ar' ? 'حرج' : 'Critical'
            };
            setActiveCardLabel(levelNames[level]);
        }
        
        setFilters(newFilters);
        setSortConfig({ field: 'sla_level', direction: 'desc' });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // ============================================

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const locale = language === 'ar' ? 'ar-EG' : 'en-US';
        return new Date(dateString).toLocaleDateString(locale, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'open': { label: t('statusOpen'), className: 'open' },
            'assigned': { label: t('statusAssigned'), className: 'assigned' },
            'in_progress': { label: t('statusInProgress'), className: 'in-progress' },
            'under_development': { label: language === 'ar' ? 'قيد التطوير' : 'Under Development', className: 'in-progress' },
            'waiting_for_customer': { label: language === 'ar' ? 'انتظار عميل' : 'Waiting Customer', className: 'waiting' },
            'solved_pending': { label: language === 'ar' ? 'تم الحل وفي انتظار التحديث' : 'Resolved – Waiting for Update', className: 'solved-pending' },
            'resolved': { label: t('statusResolved'), className: 'resolved' },
            'closed': { label: t('statusClosed'), className: 'closed' }
        };
        const s = statusMap[status] || { label: status, className: 'default' };
        return <span className={`hd-status-badge ${s.className}`}>{s.label}</span>;
    };

    const getPriorityBadge = (priority) => {
        const priorityMap = {
            'low': { label: t('priorityLow'), className: 'low' },
            'medium': { label: t('priorityMedium'), className: 'medium' },
            'high': { label: t('priorityHigh'), className: 'high' },
            'critical': { label: t('priorityCritical'), className: 'critical' }
        };
        const p = priorityMap[priority] || { label: priority, className: 'default' };
        return <span className={`hd-priority-badge ${p.className}`}>{p.label}</span>;
    };

    const getRowSlaStyle = (ticket) => {
        if (!ticket.sla_level || ticket.sla_level === 0) return {};
        const styles = {
            1: { backgroundColor: 'rgba(255, 193, 7, 0.15)', borderLeft: '4px solid #ffc107' },
            2: { backgroundColor: 'rgba(255, 152, 0, 0.2)', borderLeft: '4px solid #ff9800' },
            3: { backgroundColor: 'rgba(244, 67, 54, 0.2)', borderLeft: '4px solid #f44336' }
        };
        return styles[ticket.sla_level] || {};
    };

    const getSlaIndicator = (slaLevel) => {
        if (!slaLevel || slaLevel === 0) return null;
        const indicators = {
            1: { icon: '⏰', title: language === 'ar' ? 'متأخر' : 'Delayed' },
            2: { icon: '⚠️', title: language === 'ar' ? 'متجاوز' : 'Overdue' },
            3: { icon: '🔴', title: language === 'ar' ? 'حرج' : 'Critical' }
        };
        const ind = indicators[slaLevel];
        return ind ? <span style={{ marginLeft: '6px' }} title={ind.title}>{ind.icon}</span> : null;
    };
    
       const getStatusClass = (status) => {
        const map = {
            'open': 'open',
            'assigned': 'assigned',
            'in_progress': 'in-progress',
            'under_development': 'in-progress',
            'waiting_for_customer': 'waiting',
            'waiting_update': 'waiting',
            'solved_pending': 'solved-pending',
            'resolved': 'resolved',
            'closed': 'closed'
        };
        return map[status] || 'open';
    };


    const displaySlaStats = slaStats.isAdminOrSupervisor ? slaStats.all : slaStats.my;
    const slaLabel = slaStats.isAdminOrSupervisor 
        ? (language === 'ar' ? 'جميع التذاكر' : 'All Tickets')
        : (language === 'ar' ? 'تذاكري' : 'My Tickets');

    const statuses = [
        { value: 'open', label: t('statusOpen') },
        { value: 'assigned', label: t('statusAssigned') },
        { value: 'in_progress', label: t('statusInProgress') },
        { value: 'under_development', label: language === 'ar' ? 'قيد التطوير' : 'Under Development' },
        { value: 'waiting_for_customer', label: language === 'ar' ? 'انتظار عميل' : 'Waiting Customer' },
        { value: 'solved_pending', label: language === 'ar' ? 'انتظار التحديث' : 'Waiting Update' },
        { value: 'resolved', label: t('statusResolved') },
        { value: 'closed', label: t('statusClosed') }
    ];

    // Styles
    const miniCardStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 16px',
        borderRadius: '10px',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.1)',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    };

    const slaCardBase = {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '14px 16px',
        borderRadius: '10px',
        transition: 'all 0.2s ease',
        border: '1px solid rgba(255,255,255,0.1)',
        cursor: 'pointer'
    };

    const slaCardStyles = {
        delayed: { ...slaCardBase, background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.15), rgba(255, 193, 7, 0.05))', borderLeft: '3px solid #ffc107' },
        overdue: { ...slaCardBase, background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.15), rgba(255, 152, 0, 0.05))', borderLeft: '3px solid #ff9800' },
        critical: { ...slaCardBase, background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.15), rgba(244, 67, 54, 0.05))', borderLeft: '3px solid #f44336' },
        total: { ...slaCardBase, background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.15), rgba(156, 39, 176, 0.05))', borderLeft: '3px solid #9c27b0' }
    };

    const isCardActive = (cardType) => {
        if (!activeCardLabel) return false;
        
        switch(cardType) {
            case 'active': return activeCardLabel === 'Active' || activeCardLabel === 'نشطة';
            case 'completed': return activeCardLabel === 'Completed' || activeCardLabel === 'مكتملة';
            case 'unassigned': return filters.assigned_to === 'unassigned';
            case 'my': return filters.assigned_to === 'me';
            case 'today': return filters.date_from && filters.date_from === filters.date_to;
            case 'sla1': return filters.sla_level === '1';
            case 'sla2': return filters.sla_level === '2';
            case 'sla3': return filters.sla_level === '3';
            case 'delayed': return filters.delayed === true;
            default: return false;
        }
    };

    const getActiveCardStyle = (isActive) => isActive ? { boxShadow: '0 0 0 2px #3b82f6', transform: 'translateY(-2px)' } : {};

    const hasActiveFilters = () => {
        return filters.search || 
               filters.status.length > 0 || 
               filters.priority || 
               filters.category_id || 
               filters.department_id || 
               filters.assigned_to || 
               filters.date_from || 
               filters.date_to || 
               filters.sla_level || 
               filters.delayed ||
               filters.hide_closed;
    };

    return (
        <Layout>
            <style>{`
                @keyframes pulse-critical { 0%, 100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.4); } 50% { box-shadow: 0 0 10px 2px rgba(244, 67, 54, 0.2); } }
                .sla-card:hover, .mini-card:hover { transform: translateY(-2px); box-shadow: 0 4px 15px rgba(0,0,0,0.2); }
                @media (max-width: 1200px) { .stats-grid-compact { grid-template-columns: repeat(3, 1fr) !important; } .sla-grid-compact { grid-template-columns: repeat(2, 1fr) !important; } }
                @media (max-width: 768px) { .stats-grid-compact, .sla-grid-compact { grid-template-columns: repeat(2, 1fr) !important; } }
                @media (max-width: 480px) { .stats-grid-compact, .sla-grid-compact { grid-template-columns: 1fr !important; } }
            `}</style>
            
            <div className={`hd-dashboard ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                {/* Header */}
                <div className="hd-header">
                    <h1>🎫 {t('helpdeskDashboard')}</h1>
                    <div className="hd-header-buttons">
                        <button onClick={toggleLanguage} className="hd-btn hd-btn-lang">
                            🌐 {language === 'ar' ? 'English' : 'العربية'}
                        </button>
                        <Link to="/helpdesk/tickets/new" className="hd-btn hd-btn-success">
                            ➕ {t('createNewTicket')}
                        </Link>
                    </div>
                </div>

                {/* Active Filter Badge */}
                {hasActiveFilters() && (
                    <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{ color: '#999', fontSize: '13px' }}>{language === 'ar' ? 'الفلتر النشط:' : 'Active Filter:'}</span>
                        
                        {activeCardLabel && (
                            <span style={{ background: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6', padding: '4px 10px', borderRadius: '20px', fontSize: '12px' }}>
                                📌 {activeCardLabel}
                            </span>
                        )}
                        
                        {filters.priority && (
                            <span style={{ background: 'rgba(168, 85, 247, 0.2)', color: '#a855f7', padding: '4px 10px', borderRadius: '20px', fontSize: '12px' }}>
                                {language === 'ar' ? 'الأولوية' : 'Priority'}: {filters.priority}
                            </span>
                        )}
                        
                        {filters.category_id && (
                            <span style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', padding: '4px 10px', borderRadius: '20px', fontSize: '12px' }}>
                                {language === 'ar' ? 'الفئة' : 'Category'}: {categories.find(c => c.id == filters.category_id)?.name || filters.category_id}
                            </span>
                        )}

                        {filters.department_id && (
                            <span style={{ background: 'rgba(168, 85, 247, 0.2)', color: '#a855f7', padding: '4px 10px', borderRadius: '20px', fontSize: '12px' }}>
                                {language === 'ar' ? 'القسم' : 'Department'}: {departments.find(d => d.id == filters.department_id)?.[language === 'ar' ? 'name_ar' : 'name'] || departments.find(d => d.id == filters.department_id)?.name || filters.department_id}
                            </span>
                        )}

                        {filters.assigned_to && !activeCardLabel && (
                            <span style={{ background: 'rgba(236, 72, 153, 0.2)', color: '#ec4899', padding: '4px 10px', borderRadius: '20px', fontSize: '12px' }}>
                                {language === 'ar' ? 'التعيين' : 'Assignment'}: {filters.assigned_to === 'me' ? (language === 'ar' ? 'تذاكري' : 'My Tickets') : filters.assigned_to === 'unassigned' ? (language === 'ar' ? 'غير مسند' : 'Unassigned') : agents.find(a => a.id == filters.assigned_to)?.full_name || filters.assigned_to}
                            </span>
                        )}
                        
                        {filters.status.length > 0 && !activeCardLabel && (
                            <span style={{ background: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', padding: '4px 10px', borderRadius: '20px', fontSize: '12px' }}>
                                {language === 'ar' ? 'الحالة' : 'Status'}: {filters.status.join(', ')}
                            </span>
                        )}

                        {(filters.date_from || filters.date_to) && !activeCardLabel && (
                            <span style={{ background: 'rgba(14, 165, 233, 0.2)', color: '#0ea5e9', padding: '4px 10px', borderRadius: '20px', fontSize: '12px' }}>
                                {language === 'ar' ? 'التاريخ' : 'Date'}: {filters.date_from} - {filters.date_to}
                            </span>
                        )}

                        <button 
                            onClick={clearFilters} 
                            style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', border: 'none', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer' }}
                        >
                            ✕ {language === 'ar' ? 'مسح الكل' : 'Clear All'}
                        </button>
                        
                        <span style={{ color: '#666', fontSize: '12px', marginLeft: '10px' }}>({pagination.total} {language === 'ar' ? 'نتيجة' : 'results'})</span>
                    </div>
                )}

                {/* ✅ Compact Stats Row - NEW ORDER: Total, Active, Completed, Unassigned, My Tickets, Today */}
                <div className="stats-grid-compact" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px', marginBottom: '15px' }}>
                    {/* Total */}
                    <div className="mini-card" style={{...miniCardStyle, cursor: 'default'}}>
                        <span style={{ fontSize: '20px' }}>📊</span>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '20px', fontWeight: 700, color: '#60a5fa' }}>{stats?.total_tickets || 0}</span>
                            <span style={{ fontSize: '11px', color: '#999' }}>{t('totalTickets')}</span>
                        </div>
                    </div>
                    {/* Active (replaces Open) */}
                    <div className="mini-card" style={{...miniCardStyle, ...getActiveCardStyle(isCardActive('active'))}} onClick={handleActiveClick}>
                        <span style={{ fontSize: '20px' }}>🔥</span>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '20px', fontWeight: 700, color: '#34d399' }}>{stats?.active_tickets || 0}</span>
                            <span style={{ fontSize: '11px', color: '#999' }}>{language === 'ar' ? 'نشطة' : 'Active'}</span>
                        </div>
                    </div>
                    {/* Completed (replaces Resolved, moved next to Active) */}
                    <div className="mini-card" style={{...miniCardStyle, ...getActiveCardStyle(isCardActive('completed'))}} onClick={handleCompletedClick}>
                        <span style={{ fontSize: '20px' }}>✅</span>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '20px', fontWeight: 700, color: '#2dd4bf' }}>{stats?.completed_tickets || 0}</span>
                            <span style={{ fontSize: '11px', color: '#999' }}>{language === 'ar' ? 'مكتملة' : 'Completed'}</span>
                        </div>
                    </div>
                    {/* Unassigned */}
                    <div className="mini-card" style={{...miniCardStyle, ...getActiveCardStyle(isCardActive('unassigned'))}} onClick={handleUnassignedClick}>
                        <span style={{ fontSize: '20px' }}>⏳</span>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '20px', fontWeight: 700, color: '#fbbf24' }}>{stats?.unassigned_tickets || 0}</span>
                            <span style={{ fontSize: '11px', color: '#999' }}>{t('unassigned')}</span>
                        </div>
                    </div>
                    {/* My Tickets */}
                    <div className="mini-card" style={{...miniCardStyle, ...getActiveCardStyle(isCardActive('my'))}} onClick={handleMyTicketsClick}>
                        <span style={{ fontSize: '20px' }}>👤</span>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '20px', fontWeight: 700, color: '#a78bfa' }}>{stats?.my_open_tickets || 0}</span>
                            <span style={{ fontSize: '11px', color: '#999' }}>{t('myTickets')}</span>
                        </div>
                    </div>
                    {/* Today */}
                    <div className="mini-card" style={{...miniCardStyle, ...getActiveCardStyle(isCardActive('today'))}} onClick={handleTodayClick}>
                        <span style={{ fontSize: '20px' }}>📅</span>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '20px', fontWeight: 700, color: '#9ca3af' }}>{stats?.today_tickets || 0}</span>
                            <span style={{ fontSize: '11px', color: '#999' }}>{t('today')}</span>
                        </div>
                    </div>
                </div>

                {/* SLA Delayed Tickets Row */}
                <div style={{ marginBottom: '15px' }}>
                    <h3 style={{ color: '#fff', fontSize: '14px', fontWeight: 600, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        ⏰ {language === 'ar' ? 'التذاكر المتأخرة' : 'Delayed Tickets'} ({slaLabel})
                    </h3>
                    <div className="sla-grid-compact" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                        <div className="sla-card" style={{...slaCardStyles.delayed, ...getActiveCardStyle(isCardActive('sla1'))}} onClick={() => handleSlaCardClick(1)}>
                            <span style={{ fontSize: '22px' }}>⏰</span>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontSize: '22px', fontWeight: 700, color: '#ffc107' }}>{displaySlaStats.level1}</span>
                                <span style={{ fontSize: '11px', color: '#fff' }}>{language === 'ar' ? 'متأخر' : 'Delayed'}</span>
                            </div>
                        </div>
                        <div className="sla-card" style={{...slaCardStyles.overdue, ...getActiveCardStyle(isCardActive('sla2'))}} onClick={() => handleSlaCardClick(2)}>
                            <span style={{ fontSize: '22px' }}>⚠️</span>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontSize: '22px', fontWeight: 700, color: '#ff9800' }}>{displaySlaStats.level2}</span>
                                <span style={{ fontSize: '11px', color: '#fff' }}>{language === 'ar' ? 'متجاوز' : 'Overdue'}</span>
                            </div>
                        </div>
                        <div 
                            className="sla-card" 
                            style={{
                                ...slaCardStyles.critical, 
                                ...getActiveCardStyle(isCardActive('sla3')),
                                animation: displaySlaStats.level3 > 0 ? 'pulse-critical 2s infinite' : 'none'
                            }} 
                            onClick={() => handleSlaCardClick(3)}
                        >
                            <span style={{ fontSize: '22px' }}>🔴</span>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontSize: '22px', fontWeight: 700, color: '#f44336' }}>{displaySlaStats.level3}</span>
                                <span style={{ fontSize: '11px', color: '#fff' }}>{language === 'ar' ? 'حرج' : 'Critical'}</span>
                            </div>
                        </div>
                        <div className="sla-card" style={{...slaCardStyles.total, ...getActiveCardStyle(isCardActive('delayed'))}} onClick={() => handleSlaCardClick('all')}>
                            <span style={{ fontSize: '22px' }}>📊</span>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontSize: '22px', fontWeight: 700, color: '#9c27b0' }}>{displaySlaStats.total}</span>
                                <span style={{ fontSize: '11px', color: '#fff' }}>{language === 'ar' ? 'إجمالي' : 'Total'}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="hd-filters-compact">
                    <div className="hd-filter-row">
                        <form onSubmit={handleSearch} className="hd-search-form">
                            <input
                                type="text"
                                placeholder={t('searchTickets')}
                                value={filters.search}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                className="hd-search-input"
                            />
                            <button type="submit" className="hd-btn hd-btn-primary">{t('search')}</button>
                        </form>

                        <select value={filters.priority} onChange={(e) => handleDropdownChange('priority', e.target.value)} className="hd-select-sm">
                            <option value="">{t('allPriorities')}</option>
                            <option value="low">{t('priorityLow')}</option>
                            <option value="medium">{t('priorityMedium')}</option>
                            <option value="high">{t('priorityHigh')}</option>
                            <option value="critical">{t('priorityCritical')}</option>
                        </select>

                        <select value={filters.category_id} onChange={(e) => handleDropdownChange('category_id', e.target.value)} className="hd-select-sm">
                            <option value="">{t('allCategories')}</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>

                        <select value={filters.department_id} onChange={(e) => handleDropdownChange('department_id', e.target.value)} className="hd-select-sm">
                            <option value="">{language === 'ar' ? 'كل الأقسام' : 'All Departments'}</option>
                            {departments.map(dept => (
                                <option key={dept.id} value={dept.id}>
                                    {language === 'ar' && dept.name_ar ? dept.name_ar : dept.name}
                                </option>
                            ))}
                        </select>

                        <select value={filters.assigned_to} onChange={(e) => handleDropdownChange('assigned_to', e.target.value)} className="hd-select-sm">
                            <option value="">{t('allAssignments')}</option>
                            <option value="me">{t('myTickets')}</option>
                            <option value="unassigned">{t('unassigned')}</option>
                            {agents.map(agent => (
                                <option key={agent.id} value={agent.id}>{agent.full_name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="hd-filter-row">
                        <div className="hd-status-chips">
                            <span className="hd-filter-label">{t('status')}:</span>
                            {statuses.map(s => (
                                <label key={s.value} className={`hd-status-chip ${filters.status.includes(s.value) ? 'active' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={filters.status.includes(s.value)}
                                        onChange={() => handleStatusToggle(s.value)}
                                    />
                                    {s.label}
                                </label>
                            ))}
                        </div>

                        <div className="hd-date-range">
                            <span className="hd-filter-label">{t('date')}:</span>
                            <input type="date" value={filters.date_from} onChange={(e) => handleDropdownChange('date_from', e.target.value)} className="hd-date-input" />
                            <span>{t('to')}</span>
                            <input type="date" value={filters.date_to} onChange={(e) => handleDropdownChange('date_to', e.target.value)} className="hd-date-input" />
                        </div>

                        <label className="hd-checkbox-filter">
                            <input type="checkbox" checked={filters.hide_closed} onChange={(e) => handleHideClosedChange(e.target.checked)} />
                            {t('hideClosed')}
                        </label>

                        <button onClick={clearFilters} className="hd-btn hd-btn-secondary hd-btn-sm">
                            {t('clearFilters')}
                        </button>
                    </div>
                </div>

                {/* Tickets Table */}
                <div className="hd-table-container">
                    {loading ? (
                        <div className="hd-loading">{t('loading')}</div>
                    ) : (
                        <table className="hd-table hd-table-sortable">
                            <thead>
 <tr>
    <th onClick={() => handleSort('ticket_number')} className="hd-sortable">
        {t('ticketNumber')} {getSortIcon('ticket_number')}
    </th>
    <th onClick={() => handleSort('subject')} className="hd-sortable">
        {t('subject')} {getSortIcon('subject')}
    </th>
    <th onClick={() => handleSort('client_company')} className="hd-sortable">
        {t('company')} {getSortIcon('client_company')}
    </th>
    <th onClick={() => handleSort('status')} className="hd-sortable">
        {t('status')} {getSortIcon('status')}
    </th>
    <th onClick={() => handleSort('priority')} className="hd-sortable">
        {t('priority')} {getSortIcon('priority')}
    </th>
    <th>{t('assignedTo')}</th>
    <th onClick={() => handleSort('created_at')} className="hd-sortable">
        {t('created')} {getSortIcon('created_at')}
    </th>
    <th onClick={() => handleSort('updated_at')} className="hd-sortable">
        {language === 'ar' ? 'آخر تعديل' : 'Last Update'} {getSortIcon('updated_at')}
    </th>
    <th onClick={() => handleSort('sla_level')} className="hd-sortable">
        SLA {getSortIcon('sla_level')}
    </th>
</tr>

                            </thead>
                            <tbody>
                                {tickets.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="hd-empty">{t('noTicketsFound')}</td>
                                    </tr>
                                ) : (
                                    tickets.map(ticket => (
                                        <tr key={ticket.id} style={getRowSlaStyle(ticket)}>
                                            <td>
                                                <Link to={`/helpdesk/tickets/${ticket.id}`} className="hd-ticket-link">
                                                    #{ticket.ticket_number}
                                                </Link>
                                                {getSlaIndicator(ticket.sla_level)}
                                            </td>
                                            <td className="hd-subject">
                                                <div>{ticket.subject}</div>
                                                <span className={`hd-subject-client ${getStatusClass(ticket.status)}`}>
                                                    <span className="hd-sc-name">{ticket.client_name || t('unknown')}</span>
                                                    {ticket.client_email && <span className="hd-sc-email">{ticket.client_email}</span>}
                                                </span>
                                            </td>
                                            <td className="hd-company">{ticket.client_company || '-'}</td>
                                            <td>{getStatusBadge(ticket.status)}</td>
                                            <td>{getPriorityBadge(ticket.priority)}</td>
                                            <td className="hd-assigned">
                                                {ticket.assigned_agents || <span className="hd-unassigned">{t('unassigned')}</span>}
                                            </td>
                                            <td>{formatDate(ticket.created_at)}</td>
                                            <td>{formatDate(ticket.updated_at)}</td>
                                            <td>
                                                {ticket.sla_level > 0 ? (
                                                    <span style={{ 
                                                        padding: '2px 8px', 
                                                        borderRadius: '4px', 
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        background: ticket.sla_level === 3 ? '#f44336' : ticket.sla_level === 2 ? '#ff9800' : '#ffc107',
                                                        color: ticket.sla_level === 1 ? '#000' : '#fff'
                                                    }}>
                                                        L{ticket.sla_level}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#4ade80', fontSize: '11px' }}>✓ OK</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <div className="hd-pagination">
                        <button
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                            disabled={pagination.page === 1}
                            className="hd-btn hd-btn-sm"
                        >
                            {t('previous')}
                        </button>
                        <span className="hd-page-info">
                            {t('page')} {pagination.page} {t('of')} {pagination.pages}
                        </span>
                        <button
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                            disabled={pagination.page === pagination.pages}
                            className="hd-btn hd-btn-sm"
                        >
                            {t('next')}
                        </button>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default HelpdeskDashboard;